/**
 * VoicePlayer — premium playback for stored voice messages.
 *
 * Features:
 *   • Static waveform painted from `peaks` (computed at upload time or
 *     lazily from the audio element if peaks weren't provided).
 *   • Play / pause toggle.
 *   • Scrubbing — tap or drag on the waveform to seek.
 *   • Playback rate cycle (1x → 1.5x → 2x → 1x).
 *   • Transcript display below the waveform when `transcript` is set;
 *     otherwise an optional locked-tier teaser when `lockedHint` is set.
 *
 * Performance: peaks are extracted once on mount via decodeAudioData
 * (skipped if `peaks` prop is provided). The render loop runs on
 * <audio> timeupdate events, not requestAnimationFrame — battery-
 * friendly and good enough for a 32-bar waveform.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Play, Pause, Lock } from "@phosphor-icons/react";
import {
  decodeBlobToSamples,
  extractPeaks,
  formatDuration,
  paintWaveform,
} from "./waveform";

const STATIC_BAR_COUNT = 48;
const SPEED_OPTIONS = [1, 1.5, 2] as const;
type SpeedOption = (typeof SPEED_OPTIONS)[number];

export type VoicePlayerProps = {
  /** Public URL of the voice blob (R2 in prod, blob: URL in tests). */
  src: string;
  /** Optional pre-computed peaks (0..1). If omitted, computed lazily. */
  peaks?: number[];
  /** Known duration in seconds (server-validated). Falls back to audio metadata. */
  durationSec?: number | null;
  /** Transcript text. When present, shown collapsible under the waveform. */
  transcript?: string | null;
  /** When transcript is null AND the user is on a non-transcription tier,
   *  show this teaser CTA copy. Caller decides the gate. */
  lockedHint?: string | null;
  /** Translated labels. */
  labels: {
    play: string;
    pause: string;
    speed: string;
    transcript: string;
    transcriptShow: string;
    transcriptHide: string;
  };
  /** Cosmetic only — sender side aligns the play button left vs right. */
  side?: "outgoing" | "incoming";
  testId?: string;
};

export function VoicePlayer({
  src,
  peaks: peaksProp,
  durationSec,
  transcript,
  lockedHint,
  labels,
  side = "incoming",
  testId = "voice-player",
}: VoicePlayerProps) {
  const { t } = useTranslation("common");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [peaks, setPeaks] = useState<number[]>(peaksProp ?? []);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSec ?? 0);
  const [speed, setSpeed] = useState<SpeedOption>(1);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [scrubX, setScrubX] = useState<number | null>(null);

  // Lazy peak extraction (one-shot, only when peaks not supplied).
  useEffect(() => {
    if (peaksProp || peaks.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(src);
        const blob = await resp.blob();
        const AudioCtor =
          window.AudioContext ||
          (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioCtor) return;
        const ctx = new AudioCtor();
        const samples = await decodeBlobToSamples(blob, ctx);
        if (cancelled) return;
        setPeaks(extractPeaks(samples, STATIC_BAR_COUNT));
        ctx.close().catch(() => {
          // silent: closing twice throws on some browsers
        });
      } catch {
        // silent: failed extraction degrades to flat bars; not a blocker
        if (!cancelled) setPeaks(new Array(STATIC_BAR_COUNT).fill(0.1));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, peaksProp]);

  // Repaint when peaks / progress change.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || peaks.length === 0) return;
    const progress = duration > 0 ? currentTime / duration : 0;
    paintWaveform(canvas, peaks, {
      activeColor: "rgb(var(--accent))",
      inactiveColor: "rgba(var(--accent), 0.28)",
      progress,
      barWidth: 2.5,
      barGap: 2,
      minHeight: 2,
    });
  }, [peaks, currentTime, duration]);

  // Repaint on size change so retina + rotation render crisp.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => {
      if (peaks.length === 0) return;
      const progress = duration > 0 ? currentTime / duration : 0;
      paintWaveform(canvas, peaks, {
        activeColor: "rgb(var(--accent))",
        inactiveColor: "rgba(var(--accent), 0.28)",
        progress,
        barWidth: 2.5,
        barGap: 2,
        minHeight: 2,
      });
    });
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [peaks, duration, currentTime]);

  const toggle = useCallback(async () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      try {
        await a.play();
      } catch {
        // silent: autoplay-policy blocks need a user gesture, which we have
      }
    } else {
      a.pause();
    }
  }, []);

  const cycleSpeed = useCallback(() => {
    setSpeed((prev) => {
      const idx = SPEED_OPTIONS.indexOf(prev);
      const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
      if (audioRef.current) audioRef.current.playbackRate = next;
      return next;
    });
  }, []);

  const seekTo = useCallback(
    (clientX: number) => {
      const canvas = canvasRef.current;
      const a = audioRef.current;
      if (!canvas || !a) return;
      const rect = canvas.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const target = ratio * (duration || a.duration || 0);
      if (Number.isFinite(target)) {
        a.currentTime = target;
        setCurrentTime(target);
      }
    },
    [duration],
  );

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setScrubX(e.clientX);
    seekTo(e.clientX);
  };
  const handlePointerMove: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (scrubX === null) return;
    seekTo(e.clientX);
  };
  const handlePointerUp: React.PointerEventHandler<HTMLDivElement> = (e) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setScrubX(null);
  };

  return (
    <div
      data-testid={testId}
      className={`inline-flex items-center gap-2.5 px-3 py-2 rounded-card bg-bg-raised border border-bg-border min-w-0 max-w-full ${
        side === "outgoing" ? "ml-auto" : ""
      }`}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        data-testid={`${testId}-audio`}
        onLoadedMetadata={() => {
          const a = audioRef.current;
          if (!a) return;
          a.playbackRate = speed;
          if (Number.isFinite(a.duration) && a.duration > 0) setDuration(a.duration);
        }}
        onTimeUpdate={() => {
          const a = audioRef.current;
          if (a) setCurrentTime(a.currentTime);
        }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrentTime(0);
        }}
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? labels.pause : labels.play}
        data-testid={`${testId}-toggle`}
        className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-accent text-accent-on focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        {playing ? <Pause size={16} /> : <Play size={16} />}
      </button>
      <div className="flex flex-col min-w-0 flex-1">
        <div
          className="relative h-9 cursor-pointer touch-none select-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <canvas
            ref={canvasRef}
            aria-label={t("a11y.voice_waveform")}
            data-testid={`${testId}-waveform`}
            className="w-full h-full"
          />
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span
            data-testid={`${testId}-time`}
            className="font-mono tabular-nums text-[10.5px] text-fg-muted num-display"
          >
            {formatDuration(currentTime)} · {formatDuration(duration)}
          </span>
          <button
            type="button"
            onClick={cycleSpeed}
            aria-label={labels.speed}
            data-testid={`${testId}-speed`}
            className="text-[10.5px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-bg-surface text-fg-secondary hover:text-accent transition-colors num-display"
          >
            {speed}×
          </button>
        </div>
      </div>
      {(transcript || lockedHint) && (
        <div className="basis-full" data-testid={`${testId}-transcript-row`}>
          {transcript ? (
            <details
              className="text-[11px] text-fg-secondary"
              open={transcriptOpen}
              onToggle={(e) => setTranscriptOpen((e.currentTarget as HTMLDetailsElement).open)}
            >
              <summary
                className="cursor-pointer text-[10px] uppercase tracking-wider text-fg-muted py-1"
                data-testid={`${testId}-transcript-toggle`}
              >
                {transcriptOpen ? labels.transcriptHide : labels.transcriptShow}
              </summary>
              <div className="mt-1 leading-relaxed whitespace-pre-wrap">{transcript}</div>
            </details>
          ) : (
            <div
              data-testid={`${testId}-transcript-locked`}
              className="text-[11px] text-fg-muted flex items-center gap-1.5"
            >
              <Lock size={11} className="text-fg-muted" />
              <span>{lockedHint}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
